import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

/**
 * Determines current portal from window.location
 */
function getCurrentPortal() {
  if (typeof window === 'undefined') return 'student';
  const fullUrl = `${window.location.pathname || ''}${window.location.search || ''}${window.location.hash || ''}`;
  if (fullUrl.includes('/teacher')) return 'teacher';
  if (fullUrl.includes('/b2b')) return 'b2b';
  if (fullUrl.includes('/guardian')) return 'guardian';
  if (fullUrl.includes('/parent')) return 'parent';
  return 'student';
}

/**
 * Computes theme and background color for system bars based on current DOM state & URL
 */
export function resolveSystemBarsTheme() {
  if (typeof document === 'undefined') {
    return { isDark: false, color: '#ffffff' };
  }

  const root = document.documentElement;
  const isDark = root.classList.contains('dark') ||
    root.classList.contains('teacher-theme-dark') ||
    document.body.classList.contains('dark') ||
    document.body.classList.contains('teacher-theme-dark');

  const portal = getCurrentPortal();

  let color;
  if (isDark) {
    if (portal === 'teacher') {
      color = '#171f22'; // Teacher Dark background
    } else {
      color = '#0d1f1a'; // Budlee Dark background
    }
  } else {
    if (portal === 'teacher') {
      color = '#f6f3ef'; // Teacher Light background
    } else if (portal === 'b2b') {
      color = '#f8fafc'; // B2B Light background
    } else {
      color = '#ffffff'; // Default Light background
    }
  }

  return { isDark, color };
}

/**
 * Updates system bars (status bar + navigation bar) appearance and color across:
 * 1. Android native bridge (WindowInsetsControllerCompat for light/dark nav buttons)
 * 2. Capacitor StatusBar plugin (iOS + Android)
 * 3. HTML meta theme-color tag
 */
export function updateSystemBarsTheme(customIsDark, customColor) {
  if (typeof window === 'undefined') return;

  const resolved = resolveSystemBarsTheme();
  const isDark = typeof customIsDark === 'boolean' ? customIsDark : resolved.isDark;
  const color = customColor || resolved.color;

  // 1. Android SafeArea & Theme Bridge (Custom bridge in MainActivity.java)
  if (window.AndroidSafeAreaBridge && typeof window.AndroidSafeAreaBridge.setSystemBarsTheme === 'function') {
    try {
      window.AndroidSafeAreaBridge.setSystemBarsTheme(isDark, color);
    } catch (e) {
      console.warn('[systemBars] AndroidSafeAreaBridge error:', e);
    }
  }

  // 2. Capacitor StatusBar Plugin (iOS & standard Android)
  if (Capacitor.isPluginAvailable('StatusBar')) {
    try {
      // Style.Dark = Light content/icons (for dark background)
      // Style.Light = Dark content/icons (for light background)
      StatusBar.setStyle({
        style: isDark ? Style.Dark : Style.Light
      }).catch(() => {});

      if (Capacitor.getPlatform() === 'android') {
        StatusBar.setBackgroundColor({ color }).catch(() => {});
      }
    } catch (e) {
      console.warn('[systemBars] Capacitor StatusBar error:', e);
    }
  }

  // 3. Web / PWA meta theme-color
  try {
    let metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (!metaThemeColor) {
      metaThemeColor = document.createElement('meta');
      metaThemeColor.name = 'theme-color';
      document.head.appendChild(metaThemeColor);
    }
    metaThemeColor.setAttribute('content', color);
  } catch (_) {}

  // Keep safe-area insets updated alongside theme
  syncSystemSafeAreas();
}

/**
 * Synchronizes native safe-area insets from AndroidSafeAreaBridge to CSS custom properties.
 */
export function syncSystemSafeAreas() {
  if (typeof window === 'undefined') return;

  let top = 0;
  let bottom = 0;
  let left = 0;
  let right = 0;

  if (window.AndroidSafeAreaBridge) {
    try {
      top = window.AndroidSafeAreaBridge.getTopInset?.() || 0;
      bottom = window.AndroidSafeAreaBridge.getBottomInset?.() || 0;
      left = window.AndroidSafeAreaBridge.getLeftInset?.() || 0;
      right = window.AndroidSafeAreaBridge.getRightInset?.() || 0;
    } catch (e) {
      console.warn('[systemBars] syncSystemSafeAreas error:', e);
    }
  }

  const isNative = typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform();
  const isNativeAndroid = isNative && Capacitor.getPlatform() === 'android';
  const isNativeIOS = isNative && Capacitor.getPlatform() === 'ios';

  // Only apply fallback synthetic insets for native edge-to-edge platforms (Capacitor),
  // NEVER for standard web browsers where the browser viewport is already positioned outside system bars.
  if (isNativeAndroid) {
    if (!top || top <= 0) top = 36;
    if (!bottom || bottom <= 0) bottom = 48;
  } else if (isNativeIOS) {
    if (!top || top < 20) {
      const screenH = typeof window !== 'undefined' ? Math.max(window.screen.height, window.screen.width) : 0;
      if (screenH >= 852) {
        top = 54; // Dynamic Island (14 Pro, 15, 16)
      } else if (screenH >= 812) {
        top = 47; // Notch devices (X, 11, 12, 13, 14)
      } else {
        top = 24; // Legacy iPhones / iPads
      }
    }
    if (!bottom || bottom < 10) {
      const screenH = typeof window !== 'undefined' ? Math.max(window.screen.height, window.screen.width) : 0;
      if (screenH >= 812) {
        bottom = 34; // Home indicator
      }
    }
  }

  if (top > 0) {
    document.documentElement.style.setProperty('--safe-area-inset-top', `${top}px`);
  } else if (!isNative) {
    document.documentElement.style.removeProperty('--safe-area-inset-top');
  }

  if (bottom > 0) {
    document.documentElement.style.setProperty('--safe-area-inset-bottom', `${bottom}px`);
  } else if (!isNative) {
    document.documentElement.style.removeProperty('--safe-area-inset-bottom');
  }

  if (left > 0) {
    document.documentElement.style.setProperty('--safe-area-inset-left', `${left}px`);
  } else if (!isNative) {
    document.documentElement.style.removeProperty('--safe-area-inset-left');
  }

  if (right > 0) {
    document.documentElement.style.setProperty('--safe-area-inset-right', `${right}px`);
  } else if (!isNative) {
    document.documentElement.style.removeProperty('--safe-area-inset-right');
  }
}

let isInitialized = false;

/**
 * Sets up listeners (DOM class mutations, URL changes) to automatically keep system bars in sync.
 */
export function initSystemBarsThemeSync() {
  if (typeof window === 'undefined' || isInitialized) return;
  isInitialized = true;

  // Initial sync
  updateSystemBarsTheme();
  syncSystemSafeAreas();

  // Schedule delayed syncs to handle Android insets arriving shortly after webview launch
  [50, 150, 300, 600, 1200, 2500].forEach((delay) => {
    setTimeout(syncSystemSafeAreas, delay);
  });

  // Re-sync on DOM class changes (e.g., when dark mode is toggled)
  let debounceTimeout = null;
  const observer = new MutationObserver(() => {
    if (debounceTimeout) clearTimeout(debounceTimeout);
    debounceTimeout = setTimeout(() => {
      updateSystemBarsTheme();
      syncSystemSafeAreas();
    }, 50);
  });

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class']
  });

  // Ensure window scroll offset stays at (0, 0) for fixed-layout SPA on mobile
  const handleScrollGuard = () => {
    if (window.scrollY !== 0 || window.scrollX !== 0) {
      window.scrollTo(0, 0);
    }
  };

  // Re-sync on route/history changes and window events
  const handleUpdate = () => {
    updateSystemBarsTheme();
    syncSystemSafeAreas();
    handleScrollGuard();
  };

  window.addEventListener('scroll', handleScrollGuard, { passive: true });
  window.addEventListener('popstate', handleUpdate);
  window.addEventListener('hashchange', handleUpdate);
  window.addEventListener('resize', handleUpdate);
  window.addEventListener('orientationchange', handleUpdate);
  window.addEventListener('safeAreaInsetsChanged', handleUpdate);
  document.addEventListener('fullscreenchange', handleUpdate);
  document.addEventListener('webkitfullscreenchange', handleUpdate);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      handleUpdate();
    }
  });

  return () => {
    observer.disconnect();
    if (debounceTimeout) clearTimeout(debounceTimeout);
    window.removeEventListener('scroll', handleScrollGuard);
    window.removeEventListener('popstate', handleUpdate);
    window.removeEventListener('hashchange', handleUpdate);
    window.removeEventListener('resize', handleUpdate);
    window.removeEventListener('orientationchange', handleUpdate);
    window.removeEventListener('safeAreaInsetsChanged', handleUpdate);
    document.removeEventListener('fullscreenchange', handleUpdate);
    document.removeEventListener('webkitfullscreenchange', handleUpdate);
  };
}
